import { useContract, useExistingCustomer, useLogger, usePerson, usePersonSpecificProduct, useProduct } from '@/hooks'

import { haXProducts } from '@/utils/HaX'
/**
 *  replaceExistingProductWithSelected
 */
function getReplaceableExistingProductsFromContract(person, productId) {
  if (!person.partnerNumber) return []

  const { getSelectedProductsOfContractPerson } = useContract()
  const { getGroupFromProduct } = useProduct()
  const groupSelected = getGroupFromProduct(productId)

  // No need to delete existing product if group of selected product is not exclusive
  if (!groupSelected.exclusive) return []

  const contractProducts = getSelectedProductsOfContractPerson(person.partnerNumber)

  const contractGroupId = product =>
    Object.keys(haXProducts.groups).find(groupId => haXProducts.groups[groupId].products.includes(product.productId))

  return contractProducts
    .filter(contractProduct => contractGroupId(contractProduct) === groupSelected.id)
    .reduce(extractProductIdFromProduct, [])
}

function getReplaceableProductsFromBasket(personId, productId) {
  const { getProductsFromCategory, getProductsFromGroup } = usePersonSpecificProduct()
  const { getCategoryFromProduct, getGroupFromProduct } = useProduct()
  const category = getCategoryFromProduct(productId)
  const group = getGroupFromProduct(productId)

  if (category?.exclusive) {
    return getProductsFromCategory(category.id, personId)
      .filter(product => product.productId !== productId)
      .reduce(extractProductIdFromProduct, [])
  }

  if (group?.exclusive) {
    return getProductsFromGroup(group.id, personId)
      .filter(product => product.productId !== productId)
      .reduce(extractProductIdFromProduct, [])
  }

  return []
}

/**
 *  mutateProductsInBasketForPerson
 *  @param {string} personId
 *  @param {string} productId
 *  @param {{mode: string, basket: Basket}} [options={}]
 */
export function mutateProductsInBasketForPerson(personId, productId, options = {}) {
  const { isExistingCustomer } = useExistingCustomer()
  const { getPerson } = usePerson()
  const { logInfo } = useLogger()
  const mode = options.mode ?? 'REPLACE'
  const ___basket = options.basket ?? null

  const pBasket = ___basket ? ___basket.persons.find(p => p.personId === personId) : getPerson(personId)
  const products = pBasket.products.products

  const addingProductIds = []
  const removingProductIds = []

  switch (mode) {
    case 'ADD':
      // we know for sure, that there's no product that needs to be replaced,
      // only add the selected product
      addingProductIds.push(productId)
      break

    case 'REMOVE':
      if (isExistingCustomer(personId)) {
        addingProductIds.push(...getReplaceableExistingProductsFromContract(getPerson(personId), productId))
      }
      removingProductIds.push(productId)
      break

    case 'REPLACE':
      if (isExistingCustomer(personId)) {
        removingProductIds.push(...getReplaceableExistingProductsFromContract(getPerson(personId), productId))
      }

      if (removingProductIds.length === 0) {
        removingProductIds.push(...getReplaceableProductsFromBasket(personId, productId))
      }

      addingProductIds.push(productId)
  }

  logInfo(['%cADDING PRODUCTS', 'color: green', addingProductIds])
  logInfo(['%cREMOVING PRODUCTS', 'color: green', removingProductIds])

  pBasket.products.products = mutatePersonProducts(products, addingProductIds, removingProductIds)
  return pBasket
}

function mutatePersonProducts(products, addingProductIds, removingProductIds) {
  const productKeys = Object.keys(products)
  const personProducts = {}

  productKeys.forEach(productKey => {
    if (addingProductIds.includes(productKey)) {
      personProducts[productKey] = Object.assign({}, products[productKey], { selected: true })
    } else if (removingProductIds.includes(productKey)) {
      personProducts[productKey] = Object.assign({}, products[productKey], { selected: false })
    } else {
      personProducts[productKey] = products[productKey]
    }
  })

  return personProducts
}

function extractProductIdFromProduct(acc, product) {
  acc.push(product.productId)
  return acc
}

/*
 * best logic in town
 *
 * ADD - we know for sure, that we want to add a products
 * only set selected true on selected product
 *
 * REMOVE - removing the product
 * if existing customer:
 *    getReplacedProductFromContract
 *
 * if (replacedProduct):
 *    set selected to true
 *
 * set selected to false on removed product
 *
 * REPLACE - we add a new product and maybe have to replace an existing one
 * if existing customer:
 *    getReplaceableProductFromContract
 *
 *
 * if (!replaceableProduct):
 *    getDefaultReplaceProducts
 *
 * if (replaceableProduct || defaultReplaceProducts):
 *    set selected on replaceable products to false
 *
 * set selected to true on selected product
 *
 */
